/**
 * Beowulf — „Little Red Riding Hood vs. Beowulf".
 *
 * RAGE: Beowulf začína s 1 Rage. Keď utŕží poškodenie, získa 1 Rage (najviac 3).
 * Karty Rage míňajú na silnejšie efekty. Rage je verejný — počítadlo pri hrdinovi
 * (`players[i].counters.rage`).
 */

import { neighbors } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, cardDef, dealDamage, discardRandom, drawCard, gainAction, getMap, healFighter } from '../ops.js';
import type { PlayerIndex } from '../types.js';
import { feint, skirmish } from './common.js';
import { addCounter, askBoost, askMove, counter, fighterOrNull, opponentOf } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const BEOWULF = 'beowulf';
const WIGLAF = 'wiglaf';
const RAGE = 'rage';
const MAX_RAGE = 3;
const HEIRLOOM = 'beowulf.the-ancient-heirloom';
const HEIRLOOM_BOOST = 'beowulf.the-ancient-heirloom#boost';
const WAR_KING = 'beowulf.the-war-king';
const HORN = 'beowulf.golden-drinking-horn';

export const rageOf = (ctx: Ctx, player: PlayerIndex) => counter(ctx, player, RAGE);
const gainRage = (ctx: Ctx, player: PlayerIndex, n = 1) => addCounter(ctx, player, RAGE, n, MAX_RAGE);
/** Minie Rage, ak ho je dosť. Vráti, či sa minul. */
function spendRage(ctx: Ctx, player: PlayerIndex, n: number): boolean {
  if (n <= 0) return true;
  if (rageOf(ctx, player) < n) return false;
  addCounter(ctx, player, RAGE, -n);
  return true;
}

const beowulfOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === BEOWULF) ?? null;

/** Voľba „koľko Rage minieš" — 0 až všetok (najviac `cap`). */
function askSpend(e: EffectCtx | { ctx: Ctx; player: PlayerIndex; cardId: string }, effect: string, label: string, cap = MAX_RAGE): void {
  const rage = Math.min(cap, rageOf(e.ctx, e.player));
  if (rage === 0) return;
  askChoice(e.ctx, {
    kind: 'option',
    player: e.player,
    source: e.cardId,
    label,
    optional: false,
    options: Array.from({ length: rage + 1 }, (_, n) => ({
      label: n === 0 ? 'Nič neminúť' : `Minúť ${n} Rage`,
      follow: { kind: 'effect' as const, effect, data: String(n) },
    })),
  });
}

/**
 * „Wiglaf deals 1 damage to each adjacent fighter. If Beowulf was dealt
 * damage this way, gain 1 action." Susedí aj vlastné figúrky — aj Beowulf.
 */
const remnantOfValor: CardEffect = {
  scheme: (e) => {
    const wiglaf = fighterOrNull(e.ctx, e.fighterId);
    if (wiglaf?.defId !== WIGLAF) return;
    const around = new Set(neighbors(getMap(e.ctx), wiglaf.spaceId));
    const targets = e.ctx.state.fighters.filter((f) => around.has(f.spaceId)).map((f) => f.id);
    const beowulf = beowulfOf(e.ctx, e.player);
    const before = beowulf?.hp ?? 0;
    for (const id of targets) dealDamage(e.ctx, id, 1);
    const after = beowulf ? (fighterOrNull(e.ctx, beowulf.id)?.hp ?? 0) : before;
    if (beowulf && targets.includes(beowulf.id) && after < before) gainAction(e.ctx, e.player);
  },
};

/** „IMMEDIATELY: Gain 1 Rage. DURING COMBAT: This card's value is +1 for each Rage you have." */
const epicPoem: CardEffect = {
  immediate: (e) => {
    gainRage(e.ctx, e.player);
  },
  during: (e, value) => value + rageOf(e.ctx, e.player),
};

/**
 * „This card's effects cannot be canceled. DURING COMBAT: You may spend 2 Rage
 * to make this card's value 5 instead. You may spend 1 Rage to BOOST this card.
 * (You may do both.)"
 */
const ancientHeirloom: CardEffect = {
  uncancelable: true,
  duringChoice: (e) => {
    if (rageOf(e.ctx, e.player) >= 2) {
      askChoice(e.ctx, {
        kind: 'confirm',
        player: e.player,
        source: e.cardId,
        label: 'The Ancient Heirloom: minúť 2 Rage — hodnota karty bude 5?',
        optional: true,
        effect: HEIRLOOM,
        yes: 'Minúť 2 Rage',
        no: 'Nie',
      });
    }
    // Boost za 1 Rage — ponúkne sa až po prvej voľbe, keď je jasné, koľko Rage zostalo.
    askChoice(e.ctx, {
      kind: 'auto',
      player: e.player,
      source: e.cardId,
      label: 'The Ancient Heirloom: boost',
      optional: false,
      follow: { kind: 'effect', effect: HEIRLOOM, data: 'boost' },
    });
  },
  onConfirm: (ctx, choice) => {
    const combat = ctx.state.combat;
    if (combat && spendRage(ctx, choice.player, 2)) (combat.flags ??= []).push('heirloom5');
  },
  onFollow: (ctx, choice) => {
    if (rageOf(ctx, choice.player) < 1 || !ctx.state.combat) return;
    askChoice(ctx, {
      kind: 'confirm',
      player: choice.player,
      source: choice.source,
      label: 'The Ancient Heirloom: minúť 1 Rage a boostovať túto kartu?',
      optional: true,
      effect: HEIRLOOM_BOOST,
      yes: 'Minúť 1 Rage',
      no: 'Nie',
    });
  },
  during: (e, value) => (e.ctx.state.combat?.flags?.includes('heirloom5') ? 5 : value),
};

/** Druhá polovica Heirloom — boost po minutí 1 Rage. */
const ancientHeirloomBoost: CardEffect = {
  onConfirm: (ctx, choice) => {
    const combat = ctx.state.combat;
    if (!combat || !choice.source || !spendRage(ctx, choice.player, 1)) return;
    askBoost(
      {
        ctx,
        cardId: choice.source,
        player: choice.player,
        fighterId: null,
        role: combat.attackerPlayer === choice.player ? 'attacker' : 'defender',
        opponentPlayer: null,
        opponentFighterId: null,
        won: false,
      },
      'The Ancient Heirloom: vyber kartu na boost',
    );
  },
};

/**
 * „If you attacked a sidekick and won the combat, you may spend 3 Rage to
 * defeat that sidekick." Cieľ nesie voľba sama (`data`) — po súboji už
 * `state.combat` neexistuje.
 */
const noContestExpecteth: CardEffect = {
  after: (e) => {
    const target = fighterOrNull(e.ctx, e.opponentFighterId);
    if (e.role !== 'attacker' || !e.won || target?.kind !== 'sidekick' || rageOf(e.ctx, e.player) < 3) return;
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'No Contest Expecteth: smieš minúť 3 Rage a sidekicka poraziť',
      optional: true,
      options: [
        {
          label: `Minúť 3 Rage a poraziť ${target.name}`,
          follow: { kind: 'effect', effect: 'beowulf.no-contest-expecteth', data: target.id },
        },
      ],
    });
  },
  onFollow: (ctx, choice, targetId) => {
    const target = fighterOrNull(ctx, targetId);
    if (!target || !spendRage(ctx, choice.player, 3)) return;
    dealDamage(ctx, target.id, target.hp);
  },
};

/** „DURING COMBAT: Spend any amount of Rage. This card's value is +2 for each Rage spent." */
const warKing: CardEffect = {
  duringChoice: (e) => askSpend(e, WAR_KING, 'The War King: koľko Rage minieš? (+2 za každý)'),
  onFollow: (ctx, choice, data) => {
    const combat = ctx.state.combat;
    const n = Number(data);
    if (!combat || !spendRage(ctx, choice.player, n)) return;
    const side = combat.attackerPlayer === choice.player ? 'attacker' : 'defender';
    combat.boosts ??= { attacker: 0, defender: 0 };
    combat.boosts[side] += 2 * n;
  },
};

/**
 * „IMMEDIATELY: You may spend 2 Rage to deal damage to the opposing fighter
 * equal to the printed value of their card."
 */
const equalOfGrendel: CardEffect = {
  immediate: (e) => {
    if (rageOf(e.ctx, e.player) < 2 || !e.opponentFighterId) return;
    askChoice(e.ctx, {
      kind: 'confirm',
      player: e.player,
      source: e.cardId,
      label: 'The Equal of Grendel: minúť 2 Rage — útočník dostane poškodenie vo výške vytlačenej hodnoty svojej karty?',
      optional: true,
      effect: 'beowulf.the-equal-of-grendel',
      yes: 'Minúť 2 Rage',
      no: 'Nie',
    });
  },
  onConfirm: (ctx, choice) => {
    const combat = ctx.state.combat;
    if (!combat || !spendRage(ctx, choice.player, 2)) return;
    const printed = combat.bonus ? combat.bonus.value : combat.attackCard ? cardDef(ctx, combat.attackCard).value : 0;
    dealDamage(ctx, combat.attackerFighter, printed);
  },
};

/** „Choose an opponent. They discard 1 random card. Gain Rage equal to its BOOST value." */
const vigorAndCourage: CardEffect = {
  scheme: (e) => {
    const opponent = opponentOf(e);
    if (opponent === null) return;
    const cardId = discardRandom(e.ctx, opponent, true);
    if (cardId) gainRage(e.ctx, e.player, cardDef(e.ctx, cardId).boost);
  },
};

/**
 * „Spend any amount of Rage. Choose a different effect for each Rage spent:
 * draw 2 cards; move Beowulf up to 4 spaces; Beowulf recovers 2 health."
 * `data`: „n" = koľko minúť; „left|vybrané" = ďalšia voľba efektu.
 */
const HORN_OPTIONS: Record<string, string> = {
  draw: 'Potiahnuť 2 karty',
  move: 'Presunúť Beowulfa až o 4',
  heal: 'Beowulf si vylieči 2 zdravia',
};

function askHorn(ctx: Ctx, player: PlayerIndex, source: string | null, left: number, taken: string[]): void {
  if (left <= 0) return;
  askChoice(ctx, {
    kind: 'option',
    player,
    source,
    label: `Golden Drinking Horn: vyber efekt (zostáva ${left})`,
    optional: false,
    options: Object.keys(HORN_OPTIONS)
      .filter((key) => !taken.includes(key))
      .map((key) => ({
        label: HORN_OPTIONS[key]!,
        follow: { kind: 'effect' as const, effect: HORN, data: `pick:${left}:${[...taken, key].join(',')}` },
      })),
  });
}

const goldenDrinkingHorn: CardEffect = {
  scheme: (e) => askSpend(e, HORN, 'Golden Drinking Horn: koľko Rage minieš? (za každý jeden efekt)'),
  onFollow: (ctx, choice, data) => {
    if (data.startsWith('next:')) {
      const [, left, taken] = data.split(':');
      askHorn(ctx, choice.player, choice.source, Number(left), taken ? taken.split(',') : []);
      return;
    }
    if (data.startsWith('pick:')) {
      const [, leftText, takenText] = data.split(':');
      const taken = takenText!.split(',');
      const picked = taken[taken.length - 1];
      const beowulf = beowulfOf(ctx, choice.player);
      if (picked === 'draw') {
        drawCard(ctx, choice.player);
        drawCard(ctx, choice.player);
      } else if (picked === 'heal' && beowulf) {
        healFighter(ctx, beowulf.id, 2);
      } else if (picked === 'move' && beowulf) {
        askMove(
          { ctx, cardId: choice.source ?? '', player: choice.player, fighterId: beowulf.id, role: 'scheme', opponentPlayer: null, opponentFighterId: null, won: false },
          { fighters: [beowulf.id], max: 4, label: 'Golden Drinking Horn: presuň Beowulfa až o 4' },
        );
      }
      const left = Number(leftText) - 1;
      if (left > 0) {
        // Ďalší efekt až po dohraní tohto (napr. presunu).
        askChoice(ctx, {
          kind: 'auto',
          player: choice.player,
          source: choice.source,
          label: 'Golden Drinking Horn: ďalší efekt',
          optional: false,
          follow: { kind: 'effect', effect: HORN, data: `next:${left}:${taken.join(',')}` },
        });
      }
      return;
    }
    // Prvá voľba: koľko Rage.
    const n = Math.min(Number(data), Object.keys(HORN_OPTIONS).length);
    if (!spendRage(ctx, choice.player, n)) return;
    askHorn(ctx, choice.player, choice.source, n, []);
  },
};

/** „Wiglaf may swap spaces with Beowulf." */
const hotForTheBattle: CardEffect = {
  after: (e) => {
    const wiglaf = fighterOrNull(e.ctx, e.fighterId);
    const beowulf = beowulfOf(e.ctx, e.player);
    if (wiglaf?.defId !== WIGLAF || !beowulf) return;
    askChoice(e.ctx, {
      kind: 'swap',
      player: e.player,
      source: e.cardId,
      label: 'Hot for the Battle: Wiglaf si smie vymeniť miesto s Beowulfom',
      optional: true,
      fighterId: wiglaf.id,
      candidates: [beowulf.id],
    });
  },
};

/** „If you won the combat, draw 2 cards. If you lost the combat, your opponent draws 2 cards." */
const fatalStruggle: CardEffect = {
  after: (e) => {
    const who = e.won ? e.player : e.opponentPlayer;
    if (who === null) return;
    drawCard(e.ctx, who);
    drawCard(e.ctx, who);
  },
};

export const beowulfCards: EffectRegistry = {
  'beowulf.remnant-of-valor': remnantOfValor,
  'beowulf.feint': feint,
  'beowulf.epic-poem': epicPoem,
  [HEIRLOOM]: ancientHeirloom,
  [HEIRLOOM_BOOST]: ancientHeirloomBoost,
  'beowulf.no-contest-expecteth': noContestExpecteth,
  [WAR_KING]: warKing,
  'beowulf.the-equal-of-grendel': equalOfGrendel,
  'beowulf.vigor-and-courage': vigorAndCourage,
  [HORN]: goldenDrinkingHorn,
  'beowulf.hot-for--the-battle': hotForTheBattle,
  'beowulf.fatal-struggle': fatalStruggle,
  'beowulf.skirmish': skirmish(2),
};

export const beowulfSpecial: HeroSpecial = {
  name: 'Rage',
  /** „Beowulf starts with 1 Rage." */
  setup: (ctx, player) => {
    gainRage(ctx, player);
  },
  /** „When Beowulf is dealt damage, he gains 1 Rage." (najviac 3) */
  onDamaged: (ctx, player, fighter, amount) => {
    if (fighter.defId === BEOWULF && amount > 0) gainRage(ctx, player);
  },
};
